import React from 'react';

/**
 * React.lazy с повтором загрузки чанка.
 *
 * После выкладки старые чанки с прежними хешами пропадают с сервера, и
 * открытая вкладка ловит «ChunkLoadError: Loading chunk N failed» (или
 * «Loading CSS chunk N failed»). Тогда: один повтор через 500 мс (сбой сети),
 * а если и он не удался — одна полная перезагрузка страницы за свежим
 * index.html. Отметка времени в sessionStorage не даёт уйти в цикл:
 * повторная перезагрузка — не раньше чем через 30 с после прошлой.
 * Успешная загрузка отметку снимает.
 */

const RELOAD_KEY = 'chunk_reload_at';
const RELOAD_GAP_MS = 30_000;
const RETRY_DELAY_MS = 500;

type TModule<T> = { default: T };

export function isChunkLoadError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { name?: string; message?: string };
  return err.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(err.message ?? '');
}

function readReloadAt(): number {
  try { return Number(sessionStorage.getItem(RELOAD_KEY)) || 0; } catch { return 0; }
}

function clearReloadFlag(): void {
  try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* приватный режим */ }
}

/** Загрузить модуль с одним повтором; при повторном сбое чанка — перезагрузка страницы. */
export async function loadWithRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    const mod = await factory();
    clearReloadFlag();
    return mod;
  } catch (first) {
    if (!isChunkLoadError(first)) throw first;
    await new Promise(res => window.setTimeout(res, RETRY_DELAY_MS));
    try {
      const mod = await factory();
      clearReloadFlag();
      return mod;
    } catch (second) {
      if (!isChunkLoadError(second)) throw second;
      if (Date.now() - readReloadAt() > RELOAD_GAP_MS) {
        try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* приватный режим */ }
        window.location.reload();
        // Страница перезагружается — ошибку не показываем
        return new Promise<T>(() => {});
      }
      throw second;
    }
  }
}

/** Тихая загрузка заранее (наведение, простой браузера): ошибки не важны. */
export function prefetchModule(factory: () => Promise<unknown>): void {
  factory().then(clearReloadFlag, () => {});
}

export function lazyWithRetry<T extends React.ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  factory: () => Promise<TModule<T>>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => loadWithRetry(factory));
}
